import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { AlertTriangle, Calendar, Clock, Mail, Phone, Globe, MapPin, ChevronDown, ChevronUp, Building2, Users, FileText, Send, MessageSquare, CheckCircle, ShieldCheck, FileCheck, Lock, LayoutDashboard, List, Calculator, TrendingUp, TrendingDown, RotateCcw, Play, Plus, X } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import BOQSummaryDashboard from '@/components/BOQSummaryDashboard';
import ConsultancyBillingSchedule from '@/components/boq/ConsultancyBillingSchedule';
import ProgressHeatmap, { OverallProgressBar } from '@/components/ProgressHeatmap';
// iter-360b — Featured Brand Partner placement inside published BOQ views.
import SponsorBadge from '@/components/SponsorBadge';
import QualityTierBadge from '@/components/QualityTierBadge';
// iter-370 — light Markdown → HTML for **bold** section headings the AI
// emits inside descriptions and T&C.
import { markdownHtmlProps } from '@/utils/mdRender';
// iter-371 — OTP gate overlay for admin-secured Public BOQ links.
import PublicBOQOTPGate from '@/components/PublicBOQOTPGate';
// iter-372 — Annexure section parses each line's description into
// Material / Brand / Installation columns for the client / vendor.
import { parseDescription, ANNEXURE_SECTIONS } from '@/utils/descAnnexure';

export default function PublicBOQView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'detailed'
  const [boqSortBy, setBoqSortBy] = useState('default');
  // iter-286: legend filter is now a Set so multiple categories can be picked.
  // Empty set = show everything. Otherwise the items, totals (Basic / Discount /
  // Overheads / GST / Grand) all recalculate from ONLY the selected categories.
  const [legendFilter, setLegendFilter] = useState(() => new Set());
  // iter-288: Quick Quote Bundle presets — user-saved combinations of category
  // codes (e.g. "Wet Areas = HVAC + Plumbing + Sanitary"). Now synced to the
  // backend via /api/public/boq/:token/quick-bundles so every device viewing
  // the same public BOQ link sees the same presets instantly.
  // A localStorage mirror is still kept as an offline fallback.
  const [quickBundles, setQuickBundles] = useState(() => {
    try {
      const raw = localStorage.getItem(`boq:quickBundles:${token || 'default'}`);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [quickBundleDialogOpen, setQuickBundleDialogOpen] = useState(false);
  const [quickBundleName, setQuickBundleName] = useState('');
  const [quickBundleSaving, setQuickBundleSaving] = useState(false);

  const persistBundlesCache = (next) => {
    try {
      localStorage.setItem(`boq:quickBundles:${token || 'default'}`, JSON.stringify(next));
    } catch {}
  };

  const fetchQuickBundles = async () => {
    try {
      const r = await axios.get(`${API}/public/boq/${token}/quick-bundles`);
      const bundles = r.data?.bundles || [];
      setQuickBundles(bundles);
      persistBundlesCache(bundles);
    } catch (err) {
      // Fall back silently to local cache — already in state from init.
      console.warn('Quick bundles fetch failed, using cached copy', err?.message);
    }
  };

  const saveQuickBundle = async () => {
    const name = quickBundleName.trim();
    if (!name || legendFilter.size === 0) return;
    const codes = Array.from(legendFilter);
    setQuickBundleSaving(true);
    try {
      const r = await axios.post(`${API}/public/boq/${token}/quick-bundles`, { name, codes });
      const saved = r.data?.bundle;
      const next = saved
        ? [...quickBundles.filter(b => b.name !== saved.name), saved]
        : quickBundles;
      setQuickBundles(next);
      persistBundlesCache(next);
      setQuickBundleDialogOpen(false);
      setQuickBundleName('');
    } catch (err) {
      console.error('Failed to save quick bundle', err);
      alert(err?.response?.data?.detail || 'Failed to save bundle.');
    } finally {
      setQuickBundleSaving(false);
    }
  };
  const applyQuickBundle = (bundle) => setLegendFilter(new Set(bundle.codes));
  const deleteQuickBundle = async (bundle) => {
    // Accept either a bundle object or a legacy name string for safety.
    const target = typeof bundle === 'string'
      ? quickBundles.find(b => b.name === bundle)
      : bundle;
    if (!target) return;
    // Optimistic UI update
    const next = quickBundles.filter(b => b.id ? b.id !== target.id : b.name !== target.name);
    setQuickBundles(next);
    persistBundlesCache(next);
    if (target.id) {
      try {
        await axios.delete(`${API}/public/boq/${token}/quick-bundles/${target.id}`);
      } catch (err) {
        console.error('Failed to delete quick bundle', err);
        // Reload truth from server on failure
        fetchQuickBundles();
      }
    }
  };
  
  // Comment form state
  const [commentForm, setCommentForm] = useState({ name: '', email: '', comment: '' });
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [commentError, setCommentError] = useState(null);
  
  // Accept BOQ form state (Client)
  const [acceptForm, setAcceptForm] = useState({ name: '', email: '', notes: '' });
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(null);
  const [acceptError, setAcceptError] = useState(null);
  
  // Accept Scope form state (Vendor)
  const [vendorAcceptForm, setVendorAcceptForm] = useState({ name: '', notes: '' });
  const [vendorAcceptSubmitting, setVendorAcceptSubmitting] = useState(false);
  const [vendorAcceptSuccess, setVendorAcceptSuccess] = useState(null);
  
  // Vendor Additional Items state
  const [categoryList, setCategoryList] = useState([]);
  const [additionalItems, setAdditionalItems] = useState([]);
  const [showAddItems, setShowAddItems] = useState(false);
  const [addItemsForm, setAddItemsForm] = useState({ vendor_name: '', vendor_email: '', notes: '', gst_percentage: 0, discount_percentage: 0 });
  const [addItemsSubmitting, setAddItemsSubmitting] = useState(false);
  const [addItemsSuccess, setAddItemsSuccess] = useState(false);
  const [newItem, setNewItem] = useState({
    main_category_code: '', main_category_name: '',
    sub_category_code: '', sub_category_name: '',
    description: '', quantity: 1, unit: 'Sqft', rate: 0
  });
  
  // What-If Analysis state
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [whatIfScenario, setWhatIfScenario] = useState({
    category_adjustments: {},
    global_rate_change: 0,
    global_quantity_change: 0
  });
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [whatIfLoading, setWhatIfLoading] = useState(false);

  useEffect(() => {
    fetchBOQ();
    fetchSummary();
    fetchQuickBundles();
  }, [token]);

  // Fetch categories for vendor additional items
  useEffect(() => {
    if (data?.view_type === 'vendor') {
      fetchCategories();
    }
  }, [data?.view_type]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/public/boq/${token}/categories`);
      setCategoryList(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchBOQ = async () => {
    try {
      // iter-371 — attach OTP session token if present so the OTP gate
      // returns the actual BOQ data instead of the gate stub.
      // iter-371-hotfix2 — use localStorage instead of sessionStorage so
      // a verified email survives tab close / refresh / new-tab open.
      const sessTok = (() => {
        try { return localStorage.getItem(`boq_otp_${token}`); } catch { return null; }
      })();
      // Pass through admin_preview=1 from the browser URL to the API call
      // so admins can smoke-test their gated link.
      const urlParams = new URLSearchParams(window.location.search);
      const adminPreview = urlParams.get('admin_preview');
      const qs = adminPreview ? `?admin_preview=${encodeURIComponent(adminPreview)}` : '';
      const cfg = sessTok ? { headers: { 'X-BOQ-Session': sessTok } } : {};
      const response = await axios.get(`${API}/public/boq/${token}${qs}`, cfg);
      setData(response.data);
      // Initialize all categories as expanded
      if (response.data.categories) {
        const expanded = {};
        response.data.categories.forEach((_, idx) => {
          expanded[idx] = true;
        });
        setExpandedCategories(expanded);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      // iter-371-hotfix2 — same localStorage + admin_preview passthrough
      const sessTok = (() => {
        try { return localStorage.getItem(`boq_otp_${token}`); } catch { return null; }
      })();
      const urlParams = new URLSearchParams(window.location.search);
      const adminPreview = urlParams.get('admin_preview');
      const qs = adminPreview ? `?admin_preview=${encodeURIComponent(adminPreview)}` : '';
      const cfg = sessTok ? { headers: { 'X-BOQ-Session': sessTok } } : {};
      const response = await axios.get(`${API}/public/boq/${token}/summary${qs}`, cfg);
      setSummaryData(response.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  // What-If Analysis - Calculate projected costs with adjustments
  const runWhatIfAnalysis = async () => {
    setWhatIfLoading(true);
    try {
      const response = await axios.post(`${API}/public/boq/${token}/what-if-analysis`, whatIfScenario);
      setWhatIfResult(response.data);
    } catch (err) {
      console.error('What-If analysis failed:', err);
    } finally {
      setWhatIfLoading(false);
    }
  };
  
  const resetWhatIfScenario = () => {
    setWhatIfScenario({
      category_adjustments: {},
      global_rate_change: 0,
      global_quantity_change: 0
    });
    setWhatIfResult(null);
  };
  
  const updateCategoryAdjustment = (code, value) => {
    setWhatIfScenario(prev => ({
      ...prev,
      category_adjustments: {
        ...prev.category_adjustments,
        [code]: parseFloat(value) || 0
      }
    }));
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setCommentSubmitting(true);
    setCommentError(null);
    
    try {
      await axios.post(`${API}/public/boq/${token}/comment`, {
        ...commentForm,
        token
      });
      setCommentSuccess(true);
      setCommentForm({ name: '', email: '', comment: '' });
    } catch (err) {
      setCommentError(err.response?.data?.detail || 'Failed to submit comment. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  // Handle Client BOQ Acceptance
  const handleAcceptBOQ = async (e) => {
    e.preventDefault();
    if (!acceptForm.name) {
      setAcceptError('Please enter your name');
      return;
    }
    
    setAcceptSubmitting(true);
    setAcceptError(null);
    
    try {
      const response = await axios.post(`${API}/public/boq/${token}/accept`, {
        client_name: acceptForm.name,
        client_email: acceptForm.email,
        acceptance_notes: acceptForm.notes
      });
      setAcceptSuccess(response.data);
    } catch (err) {
      setAcceptError(err.response?.data?.detail || 'Failed to accept quotation. Please try again.');
    } finally {
      setAcceptSubmitting(false);
    }
  };

  // Handle Vendor Scope Acceptance
  const handleVendorAcceptScope = async (e) => {
    e.preventDefault();
    if (!vendorAcceptForm.name) {
      setAcceptError('Please enter your name');
      return;
    }
    
    setVendorAcceptSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/public/boq/${token}/vendor-accept`, {
        vendor_name: vendorAcceptForm.name,
        acceptance_notes: vendorAcceptForm.notes
      });
      setVendorAcceptSuccess(response.data);
    } catch (err) {
      setAcceptError(err.response?.data?.detail || 'Failed to accept scope. Please try again.');
    } finally {
      setVendorAcceptSubmitting(false);
    }
  };

  // Add item to additional items list
  const handleAddItem = () => {
    if (!newItem.description.trim() || !newItem.main_category_code) return;
    const amount = Math.round(newItem.quantity * newItem.rate * 100) / 100;
    setAdditionalItems(prev => [...prev, { ...newItem, amount, id: Date.now() }]);
    setNewItem({
      main_category_code: newItem.main_category_code,
      main_category_name: newItem.main_category_name,
      sub_category_code: newItem.sub_category_code,
      sub_category_name: newItem.sub_category_name,
      description: '', quantity: 1, unit: 'Sqft', rate: 0
    });
  };

  const handleRemoveItem = (id) => {
    setAdditionalItems(prev => prev.filter(item => item.id !== id));
  };

  // Submit additional items
  const handleSubmitAdditionalItems = async (e) => {
    e.preventDefault();
    if (!additionalItems.length) return;
    if (!addItemsForm.vendor_name.trim()) return;
    
    setAddItemsSubmitting(true);
    try {
      await axios.post(`${API}/public/boq/${token}/vendor-additional-items`, {
        vendor_name: addItemsForm.vendor_name,
        vendor_email: addItemsForm.vendor_email,
        items: additionalItems.map(({ id, amount, ...rest }) => rest),
        notes: addItemsForm.notes,
        gst_percentage: addItemsForm.gst_percentage || 0,
        discount_percentage: addItemsForm.discount_percentage || 0,
      });
      setAddItemsSuccess(true);
      setAdditionalItems([]);
    } catch (err) {
      console.error('Submit additional items failed:', err);
    } finally {
      setAddItemsSubmitting(false);
    }
  };

  // Get sub-categories for selected main category
  const getSubCategories = (mainCode) => {
    const mainCat = categoryList.find(c => c.code === mainCode);
    return mainCat?.sub_categories || [];
  };

  const toggleDescription = (itemKey) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const toggleCategory = (idx) => {
    setExpandedCategories(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const collapseAllCategories = () => {
    const collapsed = {};
    categories.forEach((_, idx) => { collapsed[idx] = false; });
    setExpandedCategories(collapsed);
  };

  const expandAllCategories = () => {
    const expanded = {};
    categories.forEach((_, idx) => { expanded[idx] = true; });
    setExpandedCategories(expanded);
  };

  // Truncate text and check if truncation is needed
  const shouldTruncate = (text, maxLength = 150) => {
    if (!text) return false;
    return text.length > maxLength;
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Format currency in Indian format
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // iter-286b: MUST be declared BEFORE any conditional early return so the
  // React hook-order rule (Rules of Hooks) is respected across renders.
  // When `legendFilter` has any categories picked, totals are recomputed from
  // ONLY those categories — Basic, Discount, Overheads, GST and Grand Total
  // scale linearly off the selected subtotal.
  const filteredTotals = useMemo(() => {
    if (!data || legendFilter.size === 0) return null;
    const categoriesSrc = data?.categories || [];
    // iter-359j — derive main-category code even when the backend forgot to
    // populate `main_category_code` (fallback to first alpha of `code`).
    // Otherwise Carpentry (G) items grouped under sub-codes like "G03" get
    // silently excluded from the filtered subtotal.
    const mainOf = (c) => {
      if (c.main_category_code) return c.main_category_code;
      const src = String(c.code || '');
      for (const ch of src) { if (/[A-Za-z]/.test(ch)) return ch.toUpperCase(); }
      return null;
    };
    const selectedCats = categoriesSrc.filter(c => legendFilter.has(mainOf(c)));
    const filteredSubtotal = selectedCats.reduce((s, c) => s + (Number(c.subtotal) || 0), 0);
    const filteredActualSubtotal = selectedCats.reduce(
      // iter-360b: prefer actual_subtotal only when it's non-zero. `??` was
      // returning 0 whenever no actual site work had been billed yet, which
      // made the category chip show ₹0 even though the estimated subtotal
      // was populated. Fall through to `subtotal` instead.
      (s, c) => s + (Number(c.actual_subtotal || c.subtotal) || 0), 0
    );
    const filteredBillable = selectedCats.reduce(
      (s, c) => s + (Number(c.billable_so_far) || 0), 0
    );
    if (filteredSubtotal === 0) return { subtotal: 0, grand_total: 0, all_zero: true };
    const orig = data.subtotal || 0;
    const ratio = orig > 0 ? filteredSubtotal / orig : 0;
    const actRatio = (data.actual_subtotal || data.subtotal || 0) > 0
      ? filteredActualSubtotal / (data.actual_subtotal || data.subtotal) : ratio;
    const fa = (v, r) => Math.round((Number(v) || 0) * r);
    return {
      filtered: true,
      selected_codes: Array.from(legendFilter),
      subtotal: Math.round(filteredSubtotal),
      actual_subtotal: Math.round(filteredActualSubtotal),
      discount_amount: fa(data.discount_amount, ratio),
      actual_discount_amount: fa(data.actual_discount_amount || data.discount_amount, actRatio),
      overhead_freight: fa(data.overhead_freight, ratio),
      actual_overhead_freight: fa(data.actual_overhead_freight || data.overhead_freight, actRatio),
      overhead_labour: fa(data.overhead_labour, ratio),
      actual_overhead_labour: fa(data.actual_overhead_labour || data.overhead_labour, actRatio),
      overhead_consultancy: fa(data.overhead_consultancy, ratio),
      actual_overhead_consultancy: fa(data.actual_overhead_consultancy || data.overhead_consultancy, actRatio),
      gst_total: fa(data.gst_total, ratio),
      actual_gst_total: fa(data.actual_gst_total || data.gst_total, actRatio),
      grand_total: fa(data.grand_total, ratio),
      actual_grand_total: fa(data.actual_grand_total || data.grand_total, actRatio),
      // iter-326: scoped Billable So Far when legend filter is active
      billable_so_far_total: Math.round(filteredBillable),
      billable_progress_simple_pct: (() => {
        const realItems = selectedCats.flatMap(c => (c.items || []).filter(it => !(it.item_type === 'header' || it.is_header)));
        if (realItems.length === 0) return 0;
        const sumP = realItems.reduce((s, it) => s + (Number(it.task_progress) || 0), 0);
        return Math.round(sumP / realItems.length);
      })(),
      // iter-326d: proportional cascade for filtered view
      billable_freight: Math.round(fa(data.actual_overhead_freight || data.overhead_freight, actRatio) * (filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0)),
      billable_labour: Math.round(fa(data.actual_overhead_labour || data.overhead_labour, actRatio) * (filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0)),
      billable_consultancy: Math.round(fa(data.actual_overhead_consultancy || data.overhead_consultancy, actRatio) * (filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0)),
      billable_discount: Math.round(fa(data.actual_discount_amount || data.discount_amount, actRatio) * (filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0)),
      billable_gst_total: Math.round(fa(data.actual_gst_total || data.gst_total, actRatio) * (filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0)),
      billable_grand_total: (() => {
        const br = filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0;
        const g = fa(data.actual_grand_total || data.grand_total, actRatio) * br;
        const c = fa(data.actual_overhead_consultancy || data.overhead_consultancy, actRatio) * br;
        const m = (+data.consultancy_milestone_amount || 0) * actRatio;
        return Math.round(m > 0 ? g - c + Math.max(c, m) : g);
      })(),
      // iter-330: deductions are absolute SSOT values — they apply against the
      // WHOLE BOQ, not the filtered subset. Pass through unchanged so the rows
      // still render. Payment receivable is recomputed against filtered grand.
      client_payments_applied: +data.client_payments_applied || 0,
      credit_notes_applied: +data.credit_notes_applied || 0,
      payment_receivable: Math.max(
        0,
        fa(data.actual_grand_total || data.grand_total, actRatio)
          - (+data.client_payments_applied || 0)
          - (+data.credit_notes_applied || 0)
      ),
      billable_payment_receivable: Math.max(
        0,
        Math.round(fa(data.actual_grand_total || data.grand_total, actRatio) * (filteredActualSubtotal > 0 ? filteredBillable / filteredActualSubtotal : 0))
          - (+data.client_payments_applied || 0)
          - (+data.credit_notes_applied || 0)
      ),
    };
  }, [data, legendFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-stone-900 mb-2">Quotation Not Found</h1>
          <p className="text-stone-600">{error}</p>
        </div>
      </div>
    );
  }

  if (data?.expired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">This Quotation Has Expired</h1>
          <p className="text-stone-600 mb-4">
            This quotation expired on {formatDate(new Date(data.expired_at), { longMonth: true })}.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <p className="font-medium mb-1">Need a renewed quotation?</p>
            <p>{data.contact_info}</p>
          </div>
          {data.worksheet_number && (
            <p className="mt-4 text-xs text-stone-400">Reference: {data.worksheet_number}</p>
          )}
        </div>
      </div>
    );
  }

  // iter-371 — OTP gate. If backend replies with { otp_required: true }, show
  // the email → OTP overlay. On successful verify, refetch the BOQ using the
  // session token stored in sessionStorage.
  if (data?.otp_required) {
    return (
      <PublicBOQOTPGate
        token={token}
        gateMessage={data.gate_message}
        onUnlocked={() => {
          setLoading(true);
          setData(null);
          fetchBOQ();
          fetchSummary();
        }}
      />
    );
  }

  // iter-371 — Anonymous view-limit cap reached (admins bypass automatically)
  if (data?.view_limit_reached) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-amber-50 flex items-center justify-center p-4" data-testid="public-view-limit-reached">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-10 h-10 text-amber-700" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">Access Limit Reached</h1>
          <p className="text-stone-600 mb-4">{data.message}</p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <p>{data.contact_info}</p>
          </div>
          {data.worksheet_number && (
            <p className="mt-4 text-xs text-stone-400">Reference: {data.worksheet_number}</p>
          )}
        </div>
      </div>
    );
  }

  const company = data?.company || {};
  const categories = data?.categories || [];
  const isVendorView = data?.view_type === 'vendor';

  // Resolve every total reference through this getter so the same JSX serves
  // both "all categories" and "filtered" views. (filteredTotals is declared
  // above — BEFORE the early returns — to respect React Rules of Hooks.)
  const T = filteredTotals || data || {};

  // Show Summary Dashboard if in summary view mode
  if (viewMode === 'summary' && summaryData && !summaryData.expired) {
    return (
      <div className="min-h-screen bg-stone-100 lg-scope" data-testid="public-boq-summary-root">
        {/* View Mode Toggle */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('summary')}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  viewMode === 'summary' 
                    ? 'bg-stone-900 text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
                data-testid="view-mode-summary"
              >
                <LayoutDashboard size={16} />
                Summary
              </button>
              <button
                onClick={() => setViewMode('detailed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  viewMode === 'detailed' 
                    ? 'bg-stone-900 text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
                data-testid="view-mode-detailed"
              >
                <List size={16} />
                Line Items
              </button>
            </div>
            <div className="text-xs text-stone-500">
              {isVendorView ? 'Vendor View' : 'Client View'} • {data?.worksheet_number}
            </div>
          </div>
        </div>
        
        <BOQSummaryDashboard data={summaryData} viewType={summaryData?.view_type || 'client'} publicToken={token} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 lg-scope py-3 sm:py-6 px-2 sm:px-4 lg:px-8 print:bg-white print:py-0">
      <div className="w-full">
        
        {/* View Mode Toggle - For Detailed View */}
        {summaryData && !summaryData.expired && (
          <div className="bg-white rounded-xl shadow-sm mb-4 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('summary')}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  viewMode === 'summary' 
                    ? 'bg-stone-900 text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                <LayoutDashboard size={16} />
                Summary
              </button>
              <button
                onClick={() => setViewMode('detailed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  viewMode === 'detailed' 
                    ? 'bg-stone-900 text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                <List size={16} />
                Line Items
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowWhatIf(!showWhatIf)}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  showWhatIf
                    ? 'bg-violet-600 text-white'
                    : 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200'
                }`}
                data-testid="what-if-toggle"
              >
                <Calculator size={16} />
                What-If Analysis
              </button>
              <div className="text-xs text-stone-500">
                {isVendorView ? 'Vendor View' : 'Client View'} • {data?.worksheet_number}
              </div>
            </div>
          </div>
        )}
        
        {/* ===================== WHAT-IF ANALYSIS PANEL ===================== */}
        {showWhatIf && (
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl shadow-lg mb-4 p-5 border border-violet-200" data-testid="what-if-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-600 rounded-lg">
                  <Calculator size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-violet-900">What-If Scenario Analysis</h3>
                  <p className="text-sm text-violet-600">Simulate cost changes before committing</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={resetWhatIfScenario}
                  className="px-3 py-1.5 text-sm bg-white border border-violet-200 text-violet-700 rounded-lg hover:bg-violet-50 flex items-center gap-1"
                  data-testid="what-if-reset"
                >
                  <RotateCcw size={14} /> Reset
                </button>
                <button
                  onClick={runWhatIfAnalysis}
                  disabled={whatIfLoading}
                  className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-1 disabled:opacity-50"
                  data-testid="what-if-calculate"
                >
                  <Play size={14} /> {whatIfLoading ? 'Calculating...' : 'Calculate'}
                </button>
              </div>
            </div>
            
            {/* Scenario Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Global Rate Change */}
              <div className="bg-white rounded-lg p-4 border border-violet-100">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Global Rate Change (%)
                </label>
                <input
                  type="number"
                  value={whatIfScenario.global_rate_change}
                  onChange={(e) => setWhatIfScenario(prev => ({ ...prev, global_rate_change: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  placeholder="e.g., 10 for +10%"
                  data-testid="what-if-global-rate"
                />
                <p className="text-xs text-stone-500 mt-1">Apply to all line items</p>
              </div>
              
              {/* Global Quantity Change */}
              <div className="bg-white rounded-lg p-4 border border-violet-100">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Global Quantity Change (%)
                </label>
                <input
                  type="number"
                  value={whatIfScenario.global_quantity_change}
                  onChange={(e) => setWhatIfScenario(prev => ({ ...prev, global_quantity_change: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  placeholder="e.g., -5 for -5%"
                  data-testid="what-if-global-qty"
                />
                <p className="text-xs text-stone-500 mt-1">Adjust all quantities</p>
              </div>
              
              {/* Category-Specific */}
              <div className="bg-white rounded-lg p-4 border border-violet-100">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Category Rate Adjustments
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {categories.slice(0, 5).map((cat) => (
                    <div key={cat.code} className="flex items-center gap-2">
                      <span className="text-xs font-medium text-stone-600 w-20 truncate" title={cat.name}>
                        {cat.code}
                      </span>
                      <input
                        type="number"
                        value={whatIfScenario.category_adjustments[cat.code] || ''}
                        onChange={(e) => updateCategoryAdjustment(cat.code, e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-stone-200 rounded focus:ring-1 focus:ring-violet-500"
                        placeholder="%"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Results */}
            {whatIfResult && (
              <div className="bg-white rounded-lg p-4 border border-violet-200">
                <h4 className="font-semibold text-violet-900 mb-3 flex items-center gap-2">
                  <TrendingUp size={16} /> Projected Impact
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 mb-1">Current Estimated</p>
                    <p className="text-lg font-bold text-blue-800">
                      {formatCurrency(whatIfResult.current.estimated.grand_total)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 mb-1">Current Actual</p>
                    <p className="text-lg font-bold text-green-800">
                      {formatCurrency(whatIfResult.current.actual.grand_total)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-violet-100 rounded-lg border-2 border-violet-300">
                    <p className="text-xs text-violet-600 mb-1">Projected Total</p>
                    <p className="text-xl font-bold text-violet-800">
                      {formatCurrency(whatIfResult.projected.grand_total)}
                    </p>
                  </div>
                  <div className={`text-center p-3 rounded-lg ${
                    whatIfResult.variance.from_actual > 0 ? 'bg-red-50' : 
                    whatIfResult.variance.from_actual < 0 ? 'bg-emerald-50' : 'bg-stone-50'
                  }`}>
                    <p className="text-xs text-stone-600 mb-1">Variance from Actual</p>
                    <p className={`text-lg font-bold flex items-center justify-center gap-1 ${
                      whatIfResult.variance.from_actual > 0 ? 'text-red-600' : 
                      whatIfResult.variance.from_actual < 0 ? 'text-emerald-600' : 'text-stone-600'
                    }`}>
                      {whatIfResult.variance.from_actual > 0 ? <TrendingUp size={16} /> : 
                       whatIfResult.variance.from_actual < 0 ? <TrendingDown size={16} /> : null}
                      {whatIfResult.variance.from_actual > 0 ? '+' : ''}
                      {formatCurrency(whatIfResult.variance.from_actual)}
                      <span className="text-sm">({whatIfResult.variance.percentage_from_actual}%)</span>
                    </p>
                  </div>
                </div>
                
                {/* Category Breakdown */}
                {whatIfResult.category_breakdown && whatIfResult.category_breakdown.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-violet-100">
                    <h5 className="text-sm font-medium text-violet-800 mb-2">Category Impact</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      {whatIfResult.category_breakdown.slice(0, 8).map(cat => {
                        const diff = cat.projected_total - cat.current_total;
                        return (
                          <div key={cat.code} className="flex justify-between p-2 bg-stone-50 rounded">
                            <span className="font-medium text-stone-700">{cat.code}</span>
                            <span className={diff > 0 ? 'text-red-600' : diff < 0 ? 'text-emerald-600' : 'text-stone-500'}>
                              {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* ===================== HEADER - PDF STYLE ===================== */}
        <div className="bg-white rounded-t-xl shadow-lg overflow-hidden">
          {/* Top Bar with Company Branding */}
          <div className="bg-stone-900 text-white px-6 py-4">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {company.logo && (
                  <img 
                    src={company.logo} 
                    alt={company.name} 
                    className="w-16 h-16 object-contain rounded-lg bg-white p-1" 
                  />
                )}
                <div>
                  <h1 className="text-xl font-bold tracking-wide">{company.name || 'Company'}</h1>
                  {company.tagline && <p className="text-stone-300 text-sm">{company.tagline}</p>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold tracking-wider">
                  {isVendorView ? 'VENDOR BOQ' : 'BILL OF QUANTITIES'}
                </div>
                {/* iter-338h: BOQ display_name visible above the auto-generated
                   worksheet_number. Falls back gracefully if user hasn't named it. */}
                {data.display_name && (
                  <div className="text-amber-300 text-sm mt-1 font-semibold">{data.display_name}</div>
                )}
                <div className="text-stone-300 text-sm mt-1">{data.worksheet_number}</div>
              </div>
            </div>
          </div>
          
          {/* Contact & Project Info Row */}
          <div className="px-6 py-4 border-b border-stone-200 bg-stone-50">
            <div className="flex flex-wrap justify-between gap-4">
              {/* Contact Details */}
              <div className="text-sm text-stone-600 space-y-1">
                {company.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-stone-400" /> {company.phone}
                  </div>
                )}
                {company.email && (
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-stone-400" /> {company.email}
                  </div>
                )}
                {company.website && (
                  <div className="flex items-center gap-2">
                    <Globe size={14} className="text-stone-400" /> {company.website}
                  </div>
                )}
                {company.gstin && (
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-stone-400" /> GSTIN: {company.gstin}
                  </div>
                )}
              </div>
              
              {/* Project Details */}
              <div className="text-right">
                <div className="text-lg font-semibold text-stone-900">{data.project_name}</div>
                <div className="text-sm text-stone-500">
                  {data.created_at && `Generated: ${formatDate(new Date(data.created_at))}`}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== VIEW TYPE BANNER ===================== */}
        <div className={`px-6 py-3 flex items-center gap-3 ${
          isVendorView 
            ? 'bg-purple-600 text-white' 
            : 'bg-blue-600 text-white'
        }`}>
          {isVendorView ? (
            <>
              <Users size={18} />
              <span className="font-semibold">VENDOR VIEW</span>
              <span className="text-sm opacity-90">— Showing Vendor Rates</span>
            </>
          ) : (
            <>
              <FileText size={18} />
              <span className="font-semibold">CLIENT VIEW</span>
              <span className="text-sm opacity-90">— Final quotation rates</span>
            </>
          )}
        </div>

        {/* ===================== VALIDITY BANNER ===================== */}
        {data.validity_date && (
          <div className={`px-6 py-3 flex items-center justify-between ${
            data.days_remaining > 7 
              ? 'bg-green-50 border-b border-green-200' 
              : data.days_remaining > 3 
                ? 'bg-amber-50 border-b border-amber-200'
                : 'bg-red-50 border-b border-red-200'
          }`}>
            <div className="flex items-center gap-3">
              <Calendar className={`w-5 h-5 ${
                data.days_remaining > 7 ? 'text-green-600' : data.days_remaining > 3 ? 'text-amber-600' : 'text-red-600'
              }`} />
              <div>
                <span className={`font-semibold ${
                  data.days_remaining > 7 ? 'text-green-800' : data.days_remaining > 3 ? 'text-amber-800' : 'text-red-800'
                }`}>
                  Valid until {formatDate(data.validity_date, { longMonth: true })}
                </span>
                <span className="text-sm text-stone-600 ml-2">
                  ({data.days_remaining} day{data.days_remaining !== 1 ? 's' : ''} remaining)
                </span>
              </div>
            </div>
            {data.days_remaining <= 7 && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                data.days_remaining > 3 ? 'bg-amber-200 text-amber-800' : 'bg-red-200 text-red-800'
              }`}>
                {data.days_remaining <= 3 ? 'Expiring Soon!' : 'Limited Time'}
              </span>
            )}
          </div>
        )}

        {/* ===================== ITEMS TABLE - GROUPED BY CATEGORY ===================== */}
        {/* iter-325: Public BOQ → single Overall Progress bar (no per-cell strip) */}
        {(() => {
          const allItems = (data?.categories || []).flatMap(c => (c.items || []));
          const hasProgress = allItems.some(it => (it.task_progress || 0) > 0 || it.task_progress_status === 'done');
          if (!hasProgress) return null;
          return (
            <div className="bg-white rounded-xl shadow-lg p-4 mb-4" data-testid="public-progress-bar">
              <OverallProgressBar items={allItems} label={`Execution Progress — ${data?.project_name || 'Project'}`} />
            </div>
          );
        })()}

        <div className="bg-white shadow-lg">
          {/* Category Color Legend - Full Names */}
          {data?.category_colors?.length > 0 && (() => {
            // Only show categories that actually have items in this BOQ.
            // iter-359j — fallback derives main code from first-alpha of `code`
            // so Carpentry (G) items sitting under sub-codes like G03 still
            // register in the legend and the filtered subtotal is correct.
            const usedCodes = new Set();
            (data.categories || []).forEach(cat => {
              let mc = cat.main_category_code;
              if (!mc) {
                const src = String(cat.code || '');
                for (const ch of src) { if (/[A-Za-z]/.test(ch)) { mc = ch.toUpperCase(); break; } }
              }
              if (mc) usedCodes.add(mc);
            });
            const applicableColors = data.category_colors.filter(cc => usedCodes.has(cc.code));
            if (applicableColors.length === 0) return null;
            return (
              <>
              <div className="flex flex-wrap items-center gap-2 px-6 py-3 bg-white border-b border-stone-200" data-testid="public-category-color-legend">
                <span className="text-xs font-semibold text-stone-600 mr-1">Legend:</span>
                {applicableColors.map(cc => {
                  const isActive = legendFilter.has(cc.code);
                  return (
                    <button key={cc.code}
                      onClick={() => setLegendFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(cc.code)) next.delete(cc.code); else next.add(cc.code);
                        return next;
                      })}
                      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all cursor-pointer ${isActive ? 'ring-2 ring-offset-1 shadow-sm font-semibold' : 'hover:shadow-sm'}`}
                      style={{ borderColor: cc.color, backgroundColor: isActive ? cc.color + '30' : cc.color + '10' }}
                      data-testid={`legend-filter-${cc.code}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cc.color }} />
                      {cc.name || cc.code}
                      {isActive && <span className="ml-1 text-[10px]">✓</span>}
                    </button>
                  );
                })}
                {legendFilter.size > 0 && (
                  <span className="text-xs text-stone-600 ml-1" data-testid="legend-filter-count">
                    {legendFilter.size} selected
                  </span>
                )}
                {legendFilter.size > 0 && (
                  <button onClick={() => setLegendFilter(new Set())} className="text-xs text-stone-500 hover:text-stone-700 underline ml-1" data-testid="legend-clear-filter">
                    Clear filter
                  </button>
                )}
                {/* iter-287: Save current selection as a Quick Bundle preset */}
                {legendFilter.size > 0 && (
                  <button
                    onClick={() => setQuickBundleDialogOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ml-1"
                    title="Save this combination as a reusable Quick Bundle preset"
                    data-testid="save-quick-bundle-btn"
                  >
                    💾 Save as Bundle
                  </button>
                )}
              </div>
              {/* iter-287: Saved Quick Bundles row */}
              {quickBundles.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs px-6 py-2 bg-white border-b border-stone-200" data-testid="quick-bundles-row">
                  <span className="text-stone-500 font-medium">Quick Bundles:</span>
                  {quickBundles.map(b => {
                    const active = b.codes.length === legendFilter.size && b.codes.every(c => legendFilter.has(c));
                    return (
                      <div key={b.id || b.name} className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white overflow-hidden">
                        <button
                          onClick={() => applyQuickBundle(b)}
                          className={`text-xs px-2.5 py-1 transition-all ${active ? 'bg-emerald-600 text-white font-semibold' : 'text-emerald-800 hover:bg-emerald-50'}`}
                          title={`${b.codes.length} categories: ${b.codes.join(', ')}`}
                          data-testid={`quick-bundle-${b.name.replace(/\s+/g,'-').toLowerCase()}`}
                        >
                          {b.name} <span className="opacity-70">({b.codes.length})</span>
                        </button>
                        <button
                          onClick={() => deleteQuickBundle(b)}
                          className="text-stone-400 hover:text-red-600 px-1.5 py-1 border-l border-stone-200 text-[10px]"
                          title={`Remove "${b.name}" preset`}
                          data-testid={`quick-bundle-delete-${b.name.replace(/\s+/g,'-').toLowerCase()}`}
                        >✕</button>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Quick Bundle Save Dialog */}
              {quickBundleDialogOpen && (
                <div className="px-6 py-2 bg-emerald-50/60 border-b border-emerald-300 flex flex-wrap items-center gap-2" data-testid="quick-bundle-dialog">
                  <span className="text-xs text-emerald-800 font-medium">Save {legendFilter.size} categories as:</span>
                  <input
                    type="text"
                    value={quickBundleName}
                    onChange={(e) => setQuickBundleName(e.target.value)}
                    placeholder="e.g. Wet Areas, Phase-1, MEP Scope..."
                    className="text-sm border border-emerald-300 rounded px-2 py-1 w-60"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') saveQuickBundle(); if (e.key === 'Escape') setQuickBundleDialogOpen(false); }}
                    data-testid="quick-bundle-name-input"
                  />
                  <button
                    onClick={saveQuickBundle}
                    disabled={!quickBundleName.trim() || quickBundleSaving}
                    className="text-xs font-semibold px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-stone-300 disabled:cursor-not-allowed"
                    data-testid="quick-bundle-save-confirm"
                  >{quickBundleSaving ? 'Saving…' : 'Save'}</button>
                  <button
                    onClick={() => { setQuickBundleDialogOpen(false); setQuickBundleName(''); }}
                    className="text-xs text-stone-600 hover:text-stone-800 px-2 py-1"
                  >Cancel</button>
                </div>
              )}
              </>
            );
          })()}
          {/* Collapse/Expand All + Sort Controls */}
          <div className="flex flex-wrap items-center justify-between px-3 sm:px-6 py-2 bg-stone-50 border-b border-stone-200 gap-2" data-testid="boq-controls">
            <div className="flex items-center gap-2">
              <button onClick={expandAllCategories} className="px-3 py-1.5 text-xs font-medium bg-white border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors" data-testid="expand-all-btn">
                <ChevronDown size={12} className="inline mr-1" />Expand All
              </button>
              <button onClick={collapseAllCategories} className="px-3 py-1.5 text-xs font-medium bg-white border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors" data-testid="collapse-all-btn">
                <ChevronUp size={12} className="inline mr-1" />Collapse All
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-500">Sort:</span>
              <select value={boqSortBy} onChange={(e) => setBoqSortBy(e.target.value)} className="text-xs border border-stone-300 rounded-lg px-2 py-1.5 bg-white" data-testid="boq-sort-select">
                <option value="default">Default Order</option>
                <option value="category_asc">Category A → Z</option>
                <option value="category_desc">Category Z → A</option>
                <option value="amount_desc">Amount High → Low</option>
                <option value="amount_asc">Amount Low → High</option>
              </select>
            </div>
          </div>
          {(() => {
            // iter-304d (FINAL): Walk the flat data.items[] in order. Each
            // header becomes an amber banner at its exact position. Between
            // headers we collect contiguous non-header items and emit a
            // CATEGORY CARD that holds ONLY those items. This means the same
            // logical category (e.g. "MK – Modular Kitchen") can render as
            // multiple cards if the admin placed headers inside it — exactly
            // the way the PDF / Edit BOQ already render the BOQ.
            let renderItems = [];
            const flatItems = (data?.flat_items_ordered || data?.items || []);
            // index categories by main_category_code so we can clone metadata
            const catByCode = new Map();
            (categories || []).forEach((c) => {
              const code = c.main_category_code || c.code || c.id || 'MAIN';
              catByCode.set(code, c);
            });

            let buffer = [];      // contiguous non-header items
            let bufKey = null;    // category code of items in `buffer`
            let chunkIdx = 0;

            const flushBuffer = () => {
              if (!buffer.length) return;
              const baseCat = catByCode.get(bufKey) || (categories || [])[0] || { main_category_code: bufKey, name: bufKey, color: '#475569' };
              // iter-361: freshly compute BOTH estimated subtotal and actual_subtotal
              // and billable_so_far from the buffered items. The parent baseCat
              // holds category-wide totals which mismatch when the category is
              // split into multiple chunks by inline section headers — that
              // caused the group-header row to display a wrong (often
              // single-item) amount vs. the footer. Chunk-scoped math ensures
              // the header sum matches the "Basic Total" printed at the bottom.
              const subtotal = buffer.reduce((s, it) => s + (Number(it.total) || (Number(it.quantity)||0) * (Number(it.rate)||0) || 0), 0);
              const actual_subtotal = buffer.reduce((s, it) => {
                const at = (it.actual_total !== undefined && it.actual_total !== null)
                  ? Number(it.actual_total)
                  : (Number(it.total) || (Number(it.quantity)||0) * (Number(it.rate)||0));
                return s + (Number.isFinite(at) ? at : 0);
              }, 0);
              const billable_so_far = buffer.reduce((s, it) => {
                if (it.billable_so_far !== undefined && it.billable_so_far !== null) {
                  return s + (Number(it.billable_so_far) || 0);
                }
                const at = (it.actual_total !== undefined && it.actual_total !== null)
                  ? Number(it.actual_total)
                  : (Number(it.total) || (Number(it.quantity)||0) * (Number(it.rate)||0));
                return s + Math.round((at || 0) * ((Number(it.task_progress) || 0) / 100));
              }, 0);
              renderItems.push({
                type: 'category',
                idx: chunkIdx++,
                cat: { ...baseCat, items: buffer, subtotal, actual_subtotal, billable_so_far },
              });
              buffer = [];
              bufKey = null;
            };

            flatItems.forEach((it, fi) => {
              if (it.item_type === 'header' || it.is_header) {
                flushBuffer();
                renderItems.push({
                  type: 'header',
                  title: it.task_title || it.description || 'Section',
                  key: `h-${fi}`,
                });
                return;
              }
              const code = it.main_category_code || it.main_code || it.category_code || (categories[0]?.main_category_code) || 'MAIN';
              if (bufKey !== code) {
                flushBuffer();
                bufKey = code;
              }
              // SR = row position in the full, unfiltered BOQ (same as Edit BOQ).
              buffer.push({ ...it, __sr: fi + 1 });
            });
            flushBuffer();

            // Apply sorting to category entries
            if (boqSortBy !== 'default') {
              const catEntries = renderItems.filter(e => e.type === 'category');
              const headerEntries = renderItems.filter(e => e.type === 'header');
              if (boqSortBy === 'category_asc') catEntries.sort((a, b) => (a.cat.main_category_code || a.cat.code || '').localeCompare(b.cat.main_category_code || b.cat.code || ''));
              else if (boqSortBy === 'category_desc') catEntries.sort((a, b) => (b.cat.main_category_code || b.cat.code || '').localeCompare(a.cat.main_category_code || a.cat.code || ''));
              else if (boqSortBy === 'amount_desc') catEntries.sort((a, b) => (b.cat.subtotal || 0) - (a.cat.subtotal || 0));
              else if (boqSortBy === 'amount_asc') catEntries.sort((a, b) => (a.cat.subtotal || 0) - (b.cat.subtotal || 0));
              renderItems = [...headerEntries, ...catEntries];
            }

            // Apply legend filter (multi-select)
            // Keeps each section header that has at least one matching group beneath it.
            if (legendFilter.size > 0) {
              const out = [];
              let pendingHeader = null;
              renderItems.forEach((entry) => {
                if (entry.type === 'header') { pendingHeader = entry; return; }
                if (entry.type === 'category' && !legendFilter.has(entry.cat.main_category_code || entry.cat.code)) return;
                if (pendingHeader) { out.push(pendingHeader); pendingHeader = null; }
                out.push(entry);
              });
              renderItems = out;
            }

            if (renderItems.length === 0) {
              return <div className="p-6"><p className="text-stone-500 text-center">No items to display</p></div>;
            }

            return renderItems.map((entry, renderIdx) => {
              // Header banner
              if (entry.type === 'header') {
                return (
                  <div key={`header-${renderIdx}`} className="bg-amber-100 border-y-2 border-amber-400 px-6 py-3" data-testid={`public-header-row-${renderIdx}`}>
                    <div className="flex items-center gap-2">
                      <LayoutDashboard size={16} className="text-amber-800" />
                      <span className="font-bold text-base text-amber-900 uppercase tracking-wide">{entry.title}</span>
                    </div>
                  </div>
                );
              }

              // Category group
              const category = entry.cat;
              const catIdx = entry.idx;
              const isExpanded = expandedCategories[catIdx] !== false;
              const catColor = category.color || '';

              // iter-364 — apply the admin-prescribed category color much
              // more prominently on the Vendor Published BOQ (previously the
              // page felt dull B&W). We tint the header bar background at
              // ~12% alpha, add a stronger left border, and use a
              // colour-matched chip so the vendor sees a visually distinct
              // block per category. Client view keeps the neutral stone
              // header for readability.
              const hexToRgba = (hex, alpha) => {
                if (!hex) return '';
                const h = hex.replace('#', '');
                const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
                const r = parseInt(full.slice(0, 2), 16);
                const g = parseInt(full.slice(2, 4), 16);
                const b = parseInt(full.slice(4, 6), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
              };
              const useVendorColor = isVendorView && catColor;
              const headerStyle = useVendorColor
                ? { backgroundColor: hexToRgba(catColor, 0.14), borderLeft: `6px solid ${catColor}`, borderBottom: `1px solid ${hexToRgba(catColor, 0.35)}` }
                : (catColor ? { borderLeft: `6px solid ${catColor}` } : undefined);

              return (
                <div key={`cat-${catIdx}`} className="border-b border-stone-200 last:border-b-0">
                  <button
                    onClick={() => toggleCategory(catIdx)}
                    className={`w-full px-3 sm:px-6 py-3 sm:py-4 ${useVendorColor ? 'hover:brightness-95' : 'bg-stone-100 hover:bg-stone-150'} flex items-center justify-between transition-colors`}
                    style={headerStyle}
                    data-testid={`category-toggle-${catIdx}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-bold"
                        style={{ backgroundColor: catColor || '#292524' }}>
                        {category.main_category_code || category.code || catIdx + 1}
                      </span>
                      <div className="text-left">
                        <h3 className="font-bold text-stone-900">{category.name}</h3>
                        {(() => {
                          // iter-288: Don't count orphan section headers in the
                          // "N items" badge — those are visual separators.
                          const realCount = (category.items || []).filter(it => !(it.item_type === 'header' || it.is_header)).length;
                          return (
                            <p className="text-sm text-stone-500">{realCount} item{realCount !== 1 ? 's' : ''}</p>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {(category.billable_so_far || 0) > 0 && (
                        <span className="hidden sm:flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" data-testid={`cat-billable-${catIdx}`}>
                          Billable: {formatCurrency(category.billable_so_far)}
                        </span>
                      )}
                      <span className="font-semibold text-stone-900">
                        {/* iter-360b: use || not ?? so a 0 actual_subtotal
                            (no site work billed yet) falls through to the
                            estimated subtotal instead of showing ₹0. */}
                        {formatCurrency(category.actual_subtotal || category.subtotal)}
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-stone-400" />
                      ) : (
                        <ChevronDown size={20} className="text-stone-400" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="overflow-x-hidden">
                      {/* iter-360b — Featured Brand Partner placement scoped to
                          this category. Client view: premium/minimal card.
                          Vendor view: richer card with catalogue thumb. */}
                      <div className="px-3 pt-2">
                        <SponsorBadge
                          zone={isVendorView ? 'boq_vendor_published' : 'boq_client_published'}
                          category={(category.main_category_code || category.code || '').charAt(0).toUpperCase()}
                          projectId={data?.project_id}
                          worksheetId={data?.worksheet_id}
                          variant={isVendorView ? 'rich-catalogue' : 'premium-minimal'}
                          limit={1}
                          publicToken={token}
                        />
                      </div>
                      {/* iter-359b: no horizontal scroll — the table now fits
                          the viewport width. Description and Remarks share
                          the remaining space (no explicit col width) so they
                          both flex and text WRAPS to multiple lines when the
                          content is long. */}
                      <table className="w-full table-fixed" data-testid={`category-table-${catIdx}`}>
                        <colgroup>
                          <col style={{width: 40}} />
                          <col />
                          <col style={{width: 55}} />
                          <col style={{width: 78}} />
                          <col style={{width: 64}} />
                          <col style={{width: 88}} />
                          <col style={{width: 64}} />
                          <col style={{width: 82}} />
                          <col style={{width: 88}} />
                          <col style={{width: 100}} />
                          <col style={{width: 80}} />
                          <col />
                        </colgroup>
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wide">Sr.</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wide">Description</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-stone-600 uppercase tracking-wide">Unit</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wide">Rate</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide bg-blue-50 border-l border-blue-100">
                              <div className="text-blue-700">Est.</div>
                              <div className="text-[10px] font-normal text-stone-500">(Design)</div>
                            </th>
                            <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide bg-blue-50">
                              <div className="text-blue-700">Est. Amt</div>
                            </th>
                            <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide bg-green-50 border-l border-green-100">
                              <div className="text-green-700">Actual</div>
                              <div className="text-[10px] font-normal text-stone-500">(Site)</div>
                            </th>
                            {/* iter-338d (Msg-680 Bug 3): Add Actual Rate column so the HTML / PDF
                               match the Excel export, where Rate is already exposed. */}
                            <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide bg-green-50">
                              <div className="text-green-700">Actual Rate</div>
                            </th>
                            <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide bg-green-50">
                              <div className="text-green-700">Actual Amt</div>
                            </th>
                            <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wide bg-emerald-100 border-l-2 border-emerald-400" data-testid="th-billable-so-far">
                              <div className="text-emerald-900">Billable So Far</div>
                              <div className="text-[10px] font-semibold text-emerald-700">(Rs)</div>
                            </th>
                            <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide bg-amber-50 border-l border-amber-100">
                              <div className="text-amber-700">Variance</div>
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wide">
                              Remarks
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">{(category.items || []).filter(it => !(it.item_type === 'header' || it.is_header)).map((item, itemIdx) => {
                            const itemKey = `${catIdx}-${itemIdx}`;
                            const isDescExpanded = expandedDescriptions[itemKey];
                            const description = item.description || '';
                            const needsTruncation = shouldTruncate(description);
                            const actQty = item.actual_quantity ?? item.quantity;
                            const estTotal = Number(item.total) || ((Number(item.quantity) || 0) * (Number(item.rate) || 0));
                            const actTotalRaw = Number(item.actual_total ?? item.total);
                            const actTotal = Number.isFinite(actTotalRaw) && (actTotalRaw || item.actual_total != null) ? actTotalRaw : estTotal;
                            const variance = actTotal - estTotal;

                            // Section header rows (sourced from estimate-to-BOQ conversion):
                            // render as an inline amber banner spanning the full table width
                            // so sections like "Countertop Details", "Sink & Plumbing" are
                            // clearly demarcated INSIDE the kitchen category.
                            if (item.item_type === 'header' || item.is_header) {
                              return (
                                <tr key={`hdr-${itemIdx}`} className="bg-amber-100 border-y-2 border-amber-400" data-testid={`inline-section-header-${catIdx}-${itemIdx}`}>
                                  <td colSpan={12} className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <LayoutDashboard size={14} className="text-amber-800" />
                                      <span className="font-bold text-sm text-amber-900 uppercase tracking-wide">{item.task_title || 'Section'}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            }

                            return (
                              <tr key={itemIdx} className="hover:bg-stone-50 transition-colors"
                                style={catColor ? { borderLeft: `6px solid ${catColor}` } : undefined}>
                                <td className="px-3 py-3 text-sm text-stone-500 align-top" data-testid={`public-item-sr-${item.__sr ?? itemIdx + 1}`}>
                                  {item.__sr ?? itemIdx + 1}
                                </td>
                                <td className="px-3 py-3 align-top max-w-[400px]">
                                  <div className="font-medium text-stone-900 mb-1">
                                    {item.task_title || item.category_name || 'Item'}
                                    {item.sub_category_code && (
                                      <span className="ml-2 text-xs font-normal text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">
                                        {item.sub_category_code}
                                      </span>
                                    )}
                                    {/* iter-386u-b — Quality Tier tag (public client BOQ) */}
                                    {item.quality_tier && item.quality_tier !== 'standard' && (
                                      <span className="ml-2 align-middle"><QualityTierBadge tier={item.quality_tier} size="xs" /></span>
                                    )}
                                  </div>
                                  {description && (
                                    <div className="text-sm text-stone-600">
                                      <div
                                        className={`whitespace-pre-wrap ${!isDescExpanded && needsTruncation ? 'line-clamp-2' : ''}`}
                                        dangerouslySetInnerHTML={markdownHtmlProps(
                                          isDescExpanded || !needsTruncation
                                            ? description
                                            : truncateText(description),
                                        )}
                                      />
                                      {needsTruncation && (
                                        <button
                                          onClick={() => toggleDescription(itemKey)}
                                          className="text-blue-600 hover:text-blue-800 text-xs font-medium mt-1 flex items-center gap-1"
                                          data-testid={`desc-toggle-${catIdx}-${itemIdx}`}
                                        >
                                          {isDescExpanded ? (
                                            <><ChevronUp size={12} /> Less</>
                                          ) : (
                                            <><ChevronDown size={12} /> More</>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {/* iter-323/325: Read-only progress badge with unified colour bands */}
                                  {(item.task_progress > 0 || item.task_progress_status === 'done') && (() => {
                                    const p = item.task_progress || 0;
                                    let barClass = 'bg-stone-300';
                                    let textClass = 'text-stone-600';
                                    if (p >= 100) { barClass = 'bg-purple-600'; textClass = 'text-purple-700'; }
                                    else if (p >= 90) { barClass = 'bg-blue-800'; textClass = 'text-blue-800'; }
                                    else if (p >= 75) { barClass = 'bg-green-700'; textClass = 'text-green-800'; }
                                    else if (p >= 50) { barClass = 'bg-green-400'; textClass = 'text-green-700'; }
                                    else if (p >= 25) { barClass = 'bg-red-500'; textClass = 'text-red-700'; }
                                    return (
                                      <div className="mt-2 flex items-center gap-1.5 text-xs" data-testid={`public-progress-${itemIdx}`}>
                                        <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden max-w-[120px]">
                                          <div className={`h-full transition-all ${barClass}`} style={{ width: `${Math.min(100, p)}%` }} />
                                        </div>
                                        <span className={`font-semibold ${textClass}`}>{p}%</span>
                                        {item.task_progress_source === 'vendor' && (
                                          <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-semibold border border-purple-300">Site</span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-3 py-3 text-center text-sm text-stone-600 align-top">
                                  {item.unit}
                                </td>
                                <td className="px-3 py-3 text-right text-sm text-stone-600 align-top">
                                  {formatCurrency(item.rate)}
                                </td>
                                <td className="px-3 py-3 text-center text-sm text-blue-700 align-top bg-blue-50/30 border-l border-blue-100">
                                  {item.quantity}
                                </td>
                                <td className="px-3 py-3 text-right text-sm text-blue-700 align-top bg-blue-50/30">
                                  {formatCurrency(estTotal)}
                                </td>
                                <td className="px-3 py-3 text-center text-sm text-green-700 align-top bg-green-50/30 border-l border-green-100">
                                  {actQty}
                                </td>
                                <td className="px-3 py-3 text-right text-sm text-green-700 align-top bg-green-50/30">
                                  {formatCurrency(item.actual_rate ?? item.rate)}
                                </td>
                                <td className="px-3 py-3 text-right text-sm text-green-700 align-top bg-green-50/30">
                                  {formatCurrency(actTotal)}
                                </td>
                                <td className="px-3 py-3 text-right text-sm font-bold align-top bg-emerald-50 border-l-2 border-emerald-400 text-emerald-900" data-testid={`billable-cell-${catIdx}-${itemIdx}`}>
                                  {(() => {
                                    const bsf = (item.billable_so_far !== undefined && item.billable_so_far !== null)
                                      ? item.billable_so_far
                                      : Math.round((actTotal || 0) * ((item.task_progress || 0) / 100));
                                    return formatCurrency(bsf);
                                  })()}
                                </td>
                                <td className={`px-3 py-3 text-right text-sm font-medium align-top bg-amber-50/30 border-l border-amber-100 ${
                                  variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-stone-500'
                                }`}>
                                  {Number.isFinite(variance) && variance !== 0 ? (variance > 0 ? '+' : '') + formatCurrency(variance) : '-'}
                                </td>
                                <td className="px-3 py-3 text-sm text-stone-600 align-top leading-relaxed break-words">
                                  {(() => {
                                    const rText = item.remarks || '';
                                    if (!rText) return <span className="text-stone-300">-</span>;
                                    const remarksKey = `remarks-${catIdx}-${itemIdx}`;
                                    const isRemarksExpanded = expandedDescriptions[remarksKey];
                                    const needsTrunc = rText.length > 120;
                                    return (
                                      <div className="whitespace-pre-wrap break-words">
                                        <div className={needsTrunc && !isRemarksExpanded ? 'line-clamp-3' : ''}>
                                          {rText}
                                        </div>
                                        {needsTrunc && (
                                          <button
                                            onClick={() => setExpandedDescriptions(prev => ({ ...prev, [remarksKey]: !prev[remarksKey] }))}
                                            className="text-blue-600 hover:text-blue-800 text-xs font-medium mt-1 flex items-center gap-1"
                                          >
                                            {isRemarksExpanded ? (
                                              <><ChevronUp size={12} /> Less</>
                                            ) : (
                                              <><ChevronDown size={12} /> More</>
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>

        {/* ===================== TOTALS SECTION - ESTIMATED vs ACTUAL vs VARIANCE ===================== */}
        <div className="bg-white rounded-b-xl shadow-lg overflow-hidden" data-testid="totals-section">
          {/* iter-304b: SUMMARY heading band (matches Edit BOQ + PDF/Email design language) */}
          <div className="bg-stone-900 text-amber-300 px-4 py-3 font-bold tracking-wider text-sm border-t-2 border-stone-800" data-testid="public-summary-heading">
            SUMMARY
          </div>
          <div className="border-t border-stone-300">
            {/* iter-326d: 5-column SUMMARY (Billable So Far inserted between Actual and Variance) */}
            <div className="grid grid-cols-5 bg-stone-100 border-b border-stone-300">
              <div className="px-4 py-3 font-medium text-stone-600 text-sm">Summary</div>
              <div className="px-4 py-3 text-right font-semibold text-blue-700 text-sm bg-blue-50 border-l border-blue-200">
                ESTIMATED (Design)
              </div>
              <div className="px-4 py-3 text-right font-semibold text-green-700 text-sm bg-green-50 border-l border-green-200">
                ACTUAL (Site)
              </div>
              <div className="px-4 py-3 text-right font-bold text-emerald-800 text-sm bg-emerald-100 border-l-2 border-emerald-400">
                BILLABLE SO FAR
              </div>
              <div className="px-4 py-3 text-right font-semibold text-amber-700 text-sm bg-amber-50 border-l border-amber-200">
                VARIANCE
              </div>
            </div>
            
            {/* Subtotal Row */}
            <div className="grid grid-cols-5 border-b border-stone-200">
              <div className="px-4 py-3 text-stone-600">
                Basic Total
                {filteredTotals?.filtered && <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="basic-total-filtered-badge">Filtered · {legendFilter.size} cat</span>}
              </div>
              <div className="px-4 py-3 text-right font-semibold text-blue-700 bg-blue-50/50 border-l border-blue-100" data-testid="public-basic-total-est">
                {formatCurrency(T.subtotal || 0)}
              </div>
              <div className="px-4 py-3 text-right font-semibold text-green-700 bg-green-50/50 border-l border-green-100" data-testid="public-basic-total-act">
                {formatCurrency(T.actual_subtotal || T.subtotal || 0)}
              </div>
              <div className="px-4 py-3 text-right font-bold text-emerald-800 bg-emerald-50/70 border-l-2 border-emerald-300" data-testid="public-basic-total-bsf">
                {formatCurrency(T.billable_so_far_total || 0)}
              </div>
              <div className={`px-4 py-3 text-right font-semibold border-l border-amber-100 bg-amber-50/50 ${
                ((T.actual_subtotal || T.subtotal || 0) - (T.subtotal || 0)) > 0 ? 'text-red-600' : 
                ((T.actual_subtotal || T.subtotal || 0) - (T.subtotal || 0)) < 0 ? 'text-green-600' : 'text-stone-500'
              }`}>
                {(() => {
                  const v = (T.actual_subtotal || T.subtotal || 0) - (T.subtotal || 0);
                  return v !== 0 ? (v > 0 ? '+' : '') + formatCurrency(v) : '-';
                })()}
              </div>
            </div>
            
            {/* Discount Row */}
            {(T.discount_amount > 0 || data.discount_percentage > 0) && (
              <div className="grid grid-cols-5 border-b border-stone-200 bg-green-50/30">
                <div className="px-4 py-3 text-green-700">
                  Discount {data.discount_type === 'percentage' ? `(${data.discount_percentage}%)` : ''}
                </div>
                <div className="px-4 py-3 text-right font-semibold text-green-700 bg-green-100/50 border-l border-green-200">
                  -{formatCurrency(T.discount_amount || 0)}
                </div>
                <div className="px-4 py-3 text-right font-semibold text-green-700 bg-green-100/50 border-l border-green-200">
                  -{formatCurrency(T.actual_discount_amount || T.discount_amount || 0)}
                </div>
                <div className="px-4 py-3 text-right font-semibold text-emerald-700 bg-emerald-50/60 border-l-2 border-emerald-300">
                  -{formatCurrency(T.billable_discount || 0)}
                </div>
                <div className="px-4 py-3 text-right font-semibold border-l border-amber-100 bg-amber-50/50 text-stone-500">-</div>
              </div>
            )}
            
            {/* Freight */}
            {(T.overhead_freight > 0 || T.actual_overhead_freight > 0) && (
              <div className="grid grid-cols-5 border-b border-stone-200">
                <div className="px-4 py-2 text-stone-600">Freight ({data.overheads?.freight_pct}%)</div>
                <div className="px-4 py-2 text-right font-semibold text-stone-700 bg-blue-50/30 border-l border-blue-100">+{formatCurrency(T.overhead_freight)}</div>
                <div className="px-4 py-2 text-right font-semibold text-stone-700 bg-green-50/30 border-l border-green-100">+{formatCurrency(T.actual_overhead_freight || T.overhead_freight)}</div>
                <div className="px-4 py-2 text-right font-semibold text-emerald-700 bg-emerald-50/60 border-l-2 border-emerald-300" data-testid="public-billable-freight">+{formatCurrency(T.billable_freight || 0)}</div>
                <div className={`px-4 py-2 text-right font-semibold border-l border-amber-100 bg-amber-50/50 ${
                  ((T.actual_overhead_freight || T.overhead_freight) - T.overhead_freight) > 0 ? 'text-red-600' :
                  ((T.actual_overhead_freight || T.overhead_freight) - T.overhead_freight) < 0 ? 'text-green-600' : 'text-stone-500'
                }`}>{(() => { const v = (T.actual_overhead_freight || T.overhead_freight) - T.overhead_freight; return v !== 0 ? (v > 0 ? '+' : '') + formatCurrency(v) : '-'; })()}</div>
              </div>
            )}
            {(T.overhead_labour > 0 || T.actual_overhead_labour > 0) && (
              <div className="grid grid-cols-5 border-b border-stone-200">
                <div className="px-4 py-2 text-stone-600">Labour Handling ({data.overheads?.labour_handling_pct}%)</div>
                <div className="px-4 py-2 text-right font-semibold text-stone-700 bg-blue-50/30 border-l border-blue-100">+{formatCurrency(T.overhead_labour)}</div>
                <div className="px-4 py-2 text-right font-semibold text-stone-700 bg-green-50/30 border-l border-green-100">+{formatCurrency(T.actual_overhead_labour || T.overhead_labour)}</div>
                <div className="px-4 py-2 text-right font-semibold text-emerald-700 bg-emerald-50/60 border-l-2 border-emerald-300" data-testid="public-billable-labour">+{formatCurrency(T.billable_labour || 0)}</div>
                <div className={`px-4 py-2 text-right font-semibold border-l border-amber-100 bg-amber-50/50 ${
                  ((T.actual_overhead_labour || T.overhead_labour) - T.overhead_labour) > 0 ? 'text-red-600' :
                  ((T.actual_overhead_labour || T.overhead_labour) - T.overhead_labour) < 0 ? 'text-green-600' : 'text-stone-500'
                }`}>{(() => { const v = (T.actual_overhead_labour || T.overhead_labour) - T.overhead_labour; return v !== 0 ? (v > 0 ? '+' : '') + formatCurrency(v) : '-'; })()}</div>
              </div>
            )}
            {(T.overhead_consultancy > 0 || T.actual_overhead_consultancy > 0) && (!isVendorView || data?.show_consultancy_to_vendor) && (
              <div className="grid grid-cols-5 border-b border-stone-200">
                <div className="px-4 py-2 text-stone-600">
                  Consultancy {(data.overheads?.consultancy_type === 'amount' || data.overheads?.consultancy_type === 'fixed' || data.overheads?.consultancy_type === 'value')
                    ? '(Fixed)'
                    : `(${data.overheads?.consultancy_pct}%)`}
                </div>
                <div className="px-4 py-2 text-right font-semibold text-stone-700 bg-blue-50/30 border-l border-blue-100">+{formatCurrency(T.overhead_consultancy)}</div>
                <div className="px-4 py-2 text-right font-semibold text-stone-700 bg-green-50/30 border-l border-green-100">+{formatCurrency(T.actual_overhead_consultancy || T.overhead_consultancy)}</div>
                <div className="px-4 py-2 text-right font-semibold text-emerald-700 bg-emerald-50/60 border-l-2 border-emerald-300" data-testid="public-billable-consultancy"><span className={+data?.consultancy_milestone_amount > (T.billable_consultancy || 0) ? 'line-through text-stone-400' : ''} title={+data?.consultancy_milestone_amount > (T.billable_consultancy || 0) ? 'Superseded by Consultancy Milestone (higher of the two is billed)' : undefined}>+{formatCurrency(T.billable_consultancy || 0)}</span></div>
                <div className={`px-4 py-2 text-right font-semibold border-l border-amber-100 bg-amber-50/50 ${
                  ((T.actual_overhead_consultancy || T.overhead_consultancy) - T.overhead_consultancy) > 0 ? 'text-red-600' :
                  ((T.actual_overhead_consultancy || T.overhead_consultancy) - T.overhead_consultancy) < 0 ? 'text-green-600' : 'text-stone-500'
                }`}>{(() => { const v = (T.actual_overhead_consultancy || T.overhead_consultancy) - T.overhead_consultancy; return v !== 0 ? (v > 0 ? '+' : '') + formatCurrency(v) : '-'; })()}</div>
              </div>
            )}

            {!isVendorView && +data?.consultancy_milestone_amount > 0 && (
              <div className="grid grid-cols-5 border-b border-stone-200 bg-emerald-50/30" data-testid="public-milestone-row">
                <div className="px-4 py-2 text-stone-600">
                  Consultancy Milestone
                  <span className="ml-2 text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">Stage {data.consultancy_stage} · {Math.round(+data.consultancy_milestone_pct)}%</span>
                </div>
                <div className="px-4 py-2 text-right text-stone-400 bg-blue-50/30 border-l border-blue-100">—</div>
                <div className="px-4 py-2 text-right text-stone-400 bg-green-50/30 border-l border-green-100">—</div>
                <div className="px-4 py-2 text-right font-semibold text-emerald-700 bg-emerald-50/60 border-l-2 border-emerald-300" data-testid="public-billable-milestone"><span className={+data.consultancy_milestone_amount <= (T.billable_consultancy || 0) ? 'line-through text-stone-400' : ''}>+{formatCurrency(+data.consultancy_milestone_amount)}</span></div>
                <div className="px-4 py-2 text-right text-stone-400 border-l border-amber-100 bg-amber-50/50">—</div>
              </div>
            )}

            {/* GST Row */}
            {(T.gst_total > 0 || T.actual_gst_total > 0) && (
              <div className="grid grid-cols-5 border-b border-stone-200">
                <div className="px-4 py-3 text-stone-600">GST Amount</div>
                <div className="px-4 py-3 text-right font-semibold text-blue-700 bg-blue-50/50 border-l border-blue-100">
                  {formatCurrency(T.gst_total)}
                </div>
                <div className="px-4 py-3 text-right font-semibold text-green-700 bg-green-50/50 border-l border-green-200">
                  {formatCurrency(T.actual_gst_total || T.gst_total)}
                </div>
                <div className="px-4 py-3 text-right font-semibold text-emerald-700 bg-emerald-50/60 border-l-2 border-emerald-300" data-testid="public-billable-gst">
                  {formatCurrency(T.billable_gst_total || 0)}
                </div>
                <div className={`px-4 py-3 text-right font-semibold border-l border-amber-100 bg-amber-50/50 ${
                  ((T.actual_gst_total || T.gst_total) - T.gst_total) > 0 ? 'text-red-600' :
                  ((T.actual_gst_total || T.gst_total) - T.gst_total) < 0 ? 'text-green-600' : 'text-stone-500'
                }`}>{(() => { const v = (T.actual_gst_total || T.gst_total) - T.gst_total; return v !== 0 ? (v > 0 ? '+' : '') + formatCurrency(v) : '-'; })()}</div>
              </div>
            )}
            
            {/* iter-326d: Progress badge row above Grand Total */}
            {((T.billable_so_far_total || 0) > 0) && (
              <div className="grid grid-cols-5 border-y-2 border-emerald-400 bg-emerald-100" data-testid="public-billable-row">
                <div className="px-4 py-3 text-emerald-900 font-bold">
                  Total Billable So Far
                  {typeof T.billable_progress_simple_pct === 'number' && T.billable_progress_simple_pct > 0 && (
                    <span className="ml-2 text-[11px] font-bold text-emerald-800 bg-white border border-emerald-400 px-2 py-0.5 rounded-full">
                      {T.billable_progress_simple_pct}% complete
                    </span>
                  )}
                  <span className="ml-2 text-[10px] text-emerald-700 font-medium">(basic + cascade applied proportionally)</span>
                </div>
                <div className="px-4 py-3 text-right text-stone-400 bg-blue-50/30 border-l border-blue-100">—</div>
                <div className="px-4 py-3 text-right text-stone-400 bg-green-50/30 border-l border-green-100">—</div>
                <div className="px-4 py-3 text-right font-bold text-emerald-900 bg-emerald-200 border-l-2 border-emerald-500 text-base" data-testid="public-billable-total">
                  {formatCurrency(T.billable_grand_total || T.billable_so_far_total || 0)}
                </div>
                <div className="px-4 py-3 text-right text-stone-400 bg-amber-50/50 border-l border-amber-100">—</div>
              </div>
            )}
            
            {/* Grand Total Row */}
            <div className="grid grid-cols-5 bg-stone-900 text-white">
              <div className="px-4 py-4 text-lg font-bold">
                GRAND TOTAL
                {filteredTotals?.filtered && <span className="ml-2 text-[10px] font-medium text-amber-300 bg-amber-900/40 px-2 py-0.5 rounded-full">Filtered ({legendFilter.size} cat)</span>}
              </div>
              <div className="px-4 py-4 text-right text-xl font-bold bg-blue-900/30 border-l border-blue-800" data-testid="public-grand-total-est">
                {formatCurrency(T.grand_total || T.subtotal || 0)}
              </div>
              <div className="px-4 py-4 text-right text-xl font-bold bg-green-900/30 border-l border-green-800" data-testid="public-grand-total-act">
                {formatCurrency(T.actual_grand_total || T.grand_total || T.subtotal || 0)}
              </div>
              <div className="px-4 py-4 text-right text-xl font-bold bg-emerald-900/50 border-l-2 border-emerald-600 text-emerald-200" data-testid="public-grand-total-bsf">
                {formatCurrency(T.billable_grand_total || T.billable_so_far_total || 0)}
              </div>
              <div className={`px-4 py-4 text-right text-xl font-bold border-l border-amber-800 ${
                ((T.actual_grand_total || T.grand_total || 0) - (T.grand_total || 0)) > 0 ? 'bg-red-900/30' : 
                ((T.actual_grand_total || T.grand_total || 0) - (T.grand_total || 0)) < 0 ? 'bg-green-900/30' : 'bg-amber-900/30'
              }`}>
                {(() => {
                  const v = (T.actual_grand_total || T.grand_total || 0) - (T.grand_total || 0);
                  return v !== 0 ? (v > 0 ? '+' : '') + formatCurrency(v) : '-';
                })()}
              </div>
            </div>
            {/* iter-312t/iter-330: Client Payments Applied + Credit Notes Applied + Payment Receivable
                — always rendered (SSOT mirror of Edit BOQ); Billable column also reflects
                the same deductions so the Billable Payment Receivable below is consistent. */}
            {!isVendorView && ((+T.client_payments_applied > 0) || (+T.credit_notes_applied > 0)) && (
              <>
                {(+T.client_payments_applied > 0) && (
                  <div className="grid grid-cols-5 bg-stone-50 border-t border-stone-200" data-testid="public-client-payments-row">
                    <div className="px-4 py-3 text-sm text-stone-700">Client Payments Applied <span className="text-stone-400">(−)</span></div>
                    <div className="px-4 py-3 text-right text-stone-300 border-l border-stone-200">—</div>
                    <div className="px-4 py-3 text-right text-red-600 font-semibold border-l border-stone-200" data-testid="public-client-payments-actual">−{formatCurrency(+T.client_payments_applied)}</div>
                    <div className="px-4 py-3 text-right text-red-600 font-semibold border-l-2 border-emerald-300 bg-emerald-50/60" data-testid="public-client-payments-billable">−{formatCurrency(+T.client_payments_applied)}</div>
                    <div className="px-4 py-3 text-right text-stone-400 border-l border-stone-200">—</div>
                  </div>
                )}
                {(+T.credit_notes_applied > 0) && (
                  <div className="grid grid-cols-5 bg-stone-50 border-t border-stone-200" data-testid="public-credit-notes-row">
                    <div className="px-4 py-3 text-sm text-stone-700">Credit Notes Applied <span className="text-stone-400">(−)</span></div>
                    <div className="px-4 py-3 text-right text-stone-300 border-l border-stone-200">—</div>
                    <div className="px-4 py-3 text-right text-red-600 font-semibold border-l border-stone-200" data-testid="public-credit-notes-actual">−{formatCurrency(+T.credit_notes_applied)}</div>
                    <div className="px-4 py-3 text-right text-red-600 font-semibold border-l-2 border-emerald-300 bg-emerald-50/60" data-testid="public-credit-notes-billable">−{formatCurrency(+T.credit_notes_applied)}</div>
                    <div className="px-4 py-3 text-right text-stone-400 border-l border-stone-200">—</div>
                  </div>
                )}
                <div className="grid grid-cols-5 bg-stone-900 text-white border-t border-stone-700" data-testid="public-payment-receivable-row">
                  <div className="px-4 py-4 text-lg font-bold text-emerald-300 uppercase tracking-wide">Payment Receivable</div>
                  <div className="px-4 py-4 text-right text-xl font-bold text-stone-500 border-l border-stone-700">—</div>
                  <div className="px-4 py-4 text-right text-xl font-bold text-emerald-300 border-l border-stone-700" data-testid="public-payment-receivable-actual">{formatCurrency(+T.payment_receivable || 0)}</div>
                  <div className="px-4 py-4 text-right text-xl font-bold text-emerald-200 border-l-2 border-emerald-400 bg-emerald-900/40" data-testid="public-billable-payment-receivable">{formatCurrency(+T.billable_payment_receivable || 0)}</div>
                  <div className="px-4 py-4 text-right text-xl font-bold text-stone-500 border-l border-stone-700">—</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ===================== OVERHEADS & T&C ===================== */}
        {(data?.overheads || data?.terms_conditions) && (
          <div className="bg-white shadow-lg mt-6 rounded-lg overflow-hidden">
            {/* Overhead costs */}
            {data?.overheads && (data.overheads.freight_pct > 0 || data.overheads.labour_handling_pct > 0 || data.overheads.consultancy_pct > 0) && (
              <div className="px-6 py-4 border-b border-stone-200">
                <h3 className="font-bold text-stone-800 mb-3">Overhead Costs</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  {data.overheads.freight_pct > 0 && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span className="text-stone-600">Freight ({data.overheads.freight_pct}%)</span>
                      <span className="font-medium">{formatCurrency(data.overhead_freight || 0)}</span>
                    </div>
                  )}
                  {data.overheads.labour_handling_pct > 0 && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span className="text-stone-600">Labour Handling ({data.overheads.labour_handling_pct}%)</span>
                      <span className="font-medium">{formatCurrency(data.overhead_labour || 0)}</span>
                    </div>
                  )}
                  {data.overheads.consultancy_pct > 0 && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span className="text-stone-600">Consultancy ({data.overheads.consultancy_pct}%)</span>
                      <span className="font-medium">{formatCurrency(data.overhead_consultancy || 0)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Terms & Conditions */}
            {data?.terms_conditions && (
              <div className="px-6 py-4">
                <h3 className="font-bold text-stone-800 mb-3">Terms & Conditions</h3>
                <div
                  className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed"
                  dangerouslySetInnerHTML={markdownHtmlProps(data.terms_conditions)}
                />
              </div>
            )}
            {/* iter-372: Annexure — parses each item's 6-section
                description into a Material / Brand / Install grid
                so the client/vendor can cross-check specs at billing time.
                iter-377: Only render when the admin has opted-in via the
                Annexure visibility toggles on Edit BOQ (per-role). */}
            {(() => {
              const annexureAllowed = isVendorView
                ? !!data?.annexure_show_to_vendor
                : !!data?.annexure_show_to_client;
              if (!annexureAllowed) return null;
              const flatItems = [];
              (data?.categories || []).forEach((cat) => {
                (cat.items || cat.line_items || []).forEach((it) => {
                  if (it.item_type === 'header') return;
                  if (!it.description || it.description.trim().length < 8) return;
                  flatItems.push({ ...it, main_category_name: cat.name || cat.main_category_name });
                });
              });
              if (flatItems.length === 0) return null;
              return (
                <div className="px-6 py-4 border-t border-stone-100" data-testid="public-annexure">
                  <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                    <FileText size={18} className="text-amber-700" /> Annexure — Material, Brand & Installation Specifications
                  </h3>
                  <div className="overflow-x-auto border border-stone-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-2 py-2 text-left font-semibold text-stone-700 border-b border-stone-200 w-8">#</th>
                          <th className="px-2 py-2 text-left font-semibold text-stone-700 border-b border-stone-200 w-56">Line Item</th>
                          {ANNEXURE_SECTIONS.map((s) => (
                            <th key={s.key} className="px-2 py-2 text-left font-semibold text-stone-700 border-b border-stone-200 border-l">
                              {s.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {flatItems.map((it, i) => {
                          const parsed = parseDescription(it.description || '');
                          return (
                            <tr key={it.id || i} className="border-b border-stone-100 align-top">
                              <td className="px-2 py-2 text-stone-500 text-center">{i + 1}</td>
                              <td className="px-2 py-2">
                                <div className="font-medium text-stone-900 line-clamp-2">{it.task_title || '—'}</div>
                                <div className="text-[10px] text-stone-500 mt-0.5">{it.main_category_name || 'Uncategorised'}</div>
                              </td>
                              {ANNEXURE_SECTIONS.map(({ key }) => (
                                <td
                                  key={key}
                                  className="px-2 py-2 border-l border-stone-100 max-w-[220px] text-stone-700 whitespace-pre-wrap"
                                  dangerouslySetInnerHTML={markdownHtmlProps(parsed[key] || '—')}
                                />
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-stone-400 mt-1">
                    Sourced from each line item&apos;s description. Highlighted keywords indicate materials, brands and Indian Standards to verify at execution and billing.
                  </p>
                </div>
              );
            })()}

            {/* iter-371: Project documents shared with this viewer's role */}
            {data?.project_documents?.length > 0 && (
              <div className="px-6 py-4 border-t border-stone-100" data-testid="public-project-documents">
                <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                  <FileText size={18} className="text-amber-700" /> Project Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.project_documents.map((d) => (
                    <a
                      key={d.id}
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-stone-200 rounded-md p-3 hover:shadow-md hover:border-amber-300 transition-all bg-white group"
                      data-testid={`public-doc-${d.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={16} className="text-stone-500 group-hover:text-amber-700" />
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                          {(d.doc_type || 'other').replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-stone-900 line-clamp-1" title={d.name}>{d.name}</div>
                      <div className="text-xs text-blue-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Open →</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===================== VENDOR SUGGESTED ADDITIONAL LINE ITEMS ===================== */}
        {isVendorView && data?.vendor_additional_items?.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-6" data-testid="vendor-suggested-items-display">
            <div className="bg-amber-600 px-4 py-3">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Plus size={20} />
                Vendor Suggested – Additional Line Items
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-amber-900">#</th>
                    <th className="text-left px-4 py-2 font-medium text-amber-900">Category</th>
                    <th className="text-left px-4 py-2 font-medium text-amber-900">Description</th>
                    <th className="text-right px-4 py-2 font-medium text-amber-900">Qty</th>
                    <th className="text-left px-4 py-2 font-medium text-amber-900">Unit</th>
                    <th className="text-right px-4 py-2 font-medium text-amber-900">Rate</th>
                    <th className="text-right px-4 py-2 font-medium text-amber-900">Amount</th>
                    <th className="text-left px-4 py-2 font-medium text-amber-900">Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let rowNum = 0;
                    let additionalSubtotal = 0;
                    return (
                      <>
                        {data.vendor_additional_items.map((sub, si) =>
                          sub.items?.map((item, ii) => {
                            rowNum++;
                            const amt = item.amount || (item.quantity * item.rate);
                            additionalSubtotal += amt;
                            return (
                              <tr key={`${si}-${ii}`} className="border-t border-amber-100 hover:bg-amber-50/50">
                                <td className="px-4 py-2 text-stone-500">{rowNum}</td>
                                <td className="px-4 py-2 text-xs text-stone-600">{item.main_category_name || item.main_category_code}{item.sub_category_name ? ` > ${item.sub_category_name}` : ''}</td>
                                <td className="px-4 py-2 text-stone-800 font-medium">{item.description}</td>
                                <td className="px-4 py-2 text-right">{item.quantity}</td>
                                <td className="px-4 py-2">{item.unit}</td>
                                <td className="px-4 py-2 text-right">₹{item.rate?.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2 text-right font-medium">₹{amt.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2 text-xs text-stone-500">{sub.vendor_name}</td>
                              </tr>
                            );
                          })
                        )}
                        {/* Additional Items Subtotal */}
                        <tr className="border-t-2 border-amber-300 bg-amber-50">
                          <td colSpan={6} className="px-4 py-3 text-right font-bold text-amber-900">Additional Items Subtotal:</td>
                          <td className="px-4 py-3 text-right font-bold text-amber-900 text-lg">
                            ₹{data.vendor_additional_items.reduce((total, sub) => total + (sub.items || []).reduce((s, i) => s + (i.amount || i.quantity * i.rate), 0), 0).toLocaleString('en-IN')}
                          </td>
                          <td></td>
                        </tr>
                        {/* FINAL TOTAL = Grand Total + Additional Items */}
                        <tr className="bg-stone-900 text-white">
                          <td colSpan={6} className="px-4 py-4 text-right font-bold text-lg">FINAL TOTAL (Grand Total + Additional Items):</td>
                          <td className="px-4 py-4 text-right font-bold text-xl">
                            ₹{((data.grand_total || data.subtotal || 0) + data.vendor_additional_items.reduce((total, sub) => total + (sub.items || []).reduce((s, i) => s + (i.amount || i.quantity * i.rate), 0), 0)).toLocaleString('en-IN')}
                          </td>
                          <td></td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===================== VENDOR: SUGGEST ADDITIONAL LINE ITEMS FORM ===================== */}
        {isVendorView && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-lg p-6 mt-6 border-2 border-amber-200" data-testid="vendor-additional-items-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-xl text-amber-800 flex items-center gap-2">
                <Plus size={24} className="text-amber-600" />
                Suggest Additional Line Items
              </h3>
              <button
                onClick={() => setShowAddItems(!showAddItems)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  showAddItems ? 'bg-amber-600 text-white' : 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-50'
                }`}
                data-testid="toggle-add-items"
              >
                {showAddItems ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showAddItems ? 'Collapse' : 'Add Items'}
              </button>
            </div>

            {addItemsSuccess ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <CheckCircle size={48} className="text-green-600 mx-auto mb-3" />
                <h4 className="font-semibold text-green-800 mb-2">Additional Items Submitted!</h4>
                <p className="text-sm text-green-700">Your suggestions have been sent to the team for review.</p>
                <button onClick={() => { setAddItemsSuccess(false); setShowAddItems(true); }} className="mt-3 text-sm text-green-600 hover:text-green-800 underline">Submit more items</button>
              </div>
            ) : showAddItems && (
              <form onSubmit={handleSubmitAdditionalItems} className="space-y-4">
                <p className="text-amber-700 text-sm">
                  Add items that were missed in the published scope. Select category to help organize items properly.
                </p>

                {/* Vendor Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">Your Name *</label>
                    <input type="text" required value={addItemsForm.vendor_name} onChange={(e) => setAddItemsForm(prev => ({ ...prev, vendor_name: e.target.value }))} placeholder="Your name or company" className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white" data-testid="add-items-vendor-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">Email</label>
                    <input type="email" value={addItemsForm.vendor_email} onChange={(e) => setAddItemsForm(prev => ({ ...prev, vendor_email: e.target.value }))} placeholder="your@email.com" className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white" data-testid="add-items-vendor-email" />
                  </div>
                </div>

                {/* Add Item Row */}
                <div className="bg-white rounded-lg p-4 border border-amber-200 space-y-3">
                  <p className="text-sm font-semibold text-stone-700">New Item Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Main Category *</label>
                      <select value={newItem.main_category_code} onChange={(e) => { const cat = categoryList.find(c => c.code === e.target.value); setNewItem(prev => ({ ...prev, main_category_code: e.target.value, main_category_name: cat?.name || '', sub_category_code: '', sub_category_name: '' })); }} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500" data-testid="add-item-main-category">
                        <option value="">Select Category</option>
                        {categoryList.map(cat => (<option key={cat.code} value={cat.code}>{cat.code} – {cat.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Sub-Category</label>
                      <select value={newItem.sub_category_code} onChange={(e) => { const subs = getSubCategories(newItem.main_category_code); const sub = subs.find(s => s.code === e.target.value); setNewItem(prev => ({ ...prev, sub_category_code: e.target.value, sub_category_name: sub?.name || '' })); }} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500" disabled={!newItem.main_category_code} data-testid="add-item-sub-category">
                        <option value="">Select Sub-Category</option>
                        {getSubCategories(newItem.main_category_code).map(sub => (<option key={sub.code} value={sub.code}>{sub.code} – {sub.name}</option>))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">Task Title / Description *</label>
                    <input type="text" value={newItem.description} onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))} placeholder="Item title / scope of work" className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500" data-testid="add-item-description" />
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Qty</label>
                      <input type="number" step="0.01" min="0" value={newItem.quantity} onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500" data-testid="add-item-quantity" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Unit</label>
                      <select value={newItem.unit} onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500" data-testid="add-item-unit">
                        {['Sqft', 'Rft', 'Cft', 'Nos', 'Kg', 'Ltr', 'Bag', 'Set', 'Lot', 'LS'].map(u => (<option key={u} value={u}>{u}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Rate (₹)</label>
                      <input type="number" step="0.01" min="0" value={newItem.rate} onChange={(e) => setNewItem(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500" data-testid="add-item-rate" />
                    </div>
                    <div className="flex items-end">
                      <button type="button" onClick={handleAddItem} disabled={!newItem.description.trim() || !newItem.main_category_code} className="w-full px-4 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center gap-1" data-testid="add-item-btn">
                        <Plus size={14} /> Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                {additionalItems.length > 0 && (
                  <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-amber-800">Category</th>
                          <th className="text-left px-3 py-2 font-medium text-amber-800">Description</th>
                          <th className="text-right px-3 py-2 font-medium text-amber-800">Qty</th>
                          <th className="text-left px-3 py-2 font-medium text-amber-800">Unit</th>
                          <th className="text-right px-3 py-2 font-medium text-amber-800">Rate</th>
                          <th className="text-right px-3 py-2 font-medium text-amber-800">Amount</th>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {additionalItems.map((item) => (
                          <tr key={item.id} className="border-t border-stone-100">
                            <td className="px-3 py-2 text-xs text-stone-600">{item.main_category_code}{item.sub_category_code ? ` / ${item.sub_category_code}` : ''}</td>
                            <td className="px-3 py-2">{item.description}</td>
                            <td className="px-3 py-2 text-right">{item.quantity}</td>
                            <td className="px-3 py-2">{item.unit}</td>
                            <td className="px-3 py-2 text-right">₹{item.rate.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right font-medium">₹{item.amount.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-2">
                              <button type="button" onClick={() => handleRemoveItem(item.id)} className="text-red-400 hover:text-red-600" data-testid={`remove-item-${item.id}`}><X size={14} /></button>
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-amber-300 bg-amber-50">
                          <td colSpan={5} className="px-3 py-2 font-semibold text-amber-800 text-right">Total:</td>
                          <td className="px-3 py-2 font-bold text-amber-900 text-right">₹{additionalItems.reduce((s, i) => s + i.amount, 0).toLocaleString('en-IN')}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* GST & Discount Row */}
                {additionalItems.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-amber-800 mb-1">GST %</label>
                      <select value={addItemsForm.gst_percentage} onChange={(e) => setAddItemsForm(prev => ({ ...prev, gst_percentage: parseFloat(e.target.value) || 0 }))} className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white" data-testid="add-items-gst">
                        <option value={0}>0% (No GST)</option>
                        <option value={5}>5%</option>
                        <option value={12}>12%</option>
                        <option value={18}>18%</option>
                        <option value={28}>28%</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-800 mb-1">Discount %</label>
                      <input type="number" min="0" max="100" step="0.5" value={addItemsForm.discount_percentage || ''} onChange={(e) => setAddItemsForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))} placeholder="0" className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white" data-testid="add-items-discount" />
                    </div>
                  </div>
                )}

                {/* Totals summary */}
                {additionalItems.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-stone-600">Subtotal:</span><span className="font-medium">₹{additionalItems.reduce((s, i) => s + i.amount, 0).toLocaleString('en-IN')}</span></div>
                    {addItemsForm.gst_percentage > 0 && (
                      <div className="flex justify-between"><span className="text-stone-600">GST ({addItemsForm.gst_percentage}%):</span><span className="font-medium">₹{Math.round(additionalItems.reduce((s, i) => s + i.amount, 0) * addItemsForm.gst_percentage / 100).toLocaleString('en-IN')}</span></div>
                    )}
                    {addItemsForm.discount_percentage > 0 && (
                      <div className="flex justify-between text-red-600"><span>Discount ({addItemsForm.discount_percentage}%):</span><span className="font-medium">-₹{Math.round(additionalItems.reduce((s, i) => s + i.amount, 0) * addItemsForm.discount_percentage / 100).toLocaleString('en-IN')}</span></div>
                    )}
                    <div className="flex justify-between border-t border-amber-200 pt-1"><span className="font-semibold text-amber-800">Grand Total:</span><span className="font-bold text-amber-900">₹{(() => { const sub = additionalItems.reduce((s, i) => s + i.amount, 0); return Math.round(sub + sub * (addItemsForm.gst_percentage || 0) / 100 - sub * (addItemsForm.discount_percentage || 0) / 100).toLocaleString('en-IN'); })()}</span></div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">Notes (Optional)</label>
                  <textarea rows={2} value={addItemsForm.notes} onChange={(e) => setAddItemsForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Any additional context about these items..." className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-white" data-testid="add-items-notes" />
                </div>

                <div className="flex justify-center pt-2">
                  <button type="submit" disabled={addItemsSubmitting || !additionalItems.length || !addItemsForm.vendor_name.trim()} className="inline-flex items-center gap-2 px-8 py-3 bg-amber-600 text-white font-bold rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl" data-testid="submit-additional-items-btn">
                    {addItemsSubmitting ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</>) : (<><Send size={16} />Submit {additionalItems.length} Additional Item{additionalItems.length !== 1 ? 's' : ''} for Review</>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ===================== NOTES & TERMS ===================== */}
        {(data.notes || data.terms) && (
          <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            {data.notes && (
              <div className="mb-4">
                <h3 className="font-semibold text-stone-900 mb-2 flex items-center gap-2">
                  <FileText size={16} /> Notes
                </h3>
                <p className="text-sm text-stone-600 whitespace-pre-wrap">{data.notes}</p>
              </div>
            )}
            {data.terms && (
              <div>
                <h3 className="font-semibold text-stone-900 mb-2">Terms & Conditions</h3>
                <p className="text-sm text-stone-600 whitespace-pre-wrap">{data.terms}</p>
              </div>
            )}
          </div>
        )}

        {/* ===================== COMMENT SECTION ===================== */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6" data-testid="comment-section">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <MessageSquare size={18} className="text-blue-600" />
            Have Questions or Comments?
          </h3>
          
          {commentSuccess ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle size={48} className="text-green-600 mx-auto mb-3" />
              <h4 className="font-semibold text-green-800 mb-2">Comment Submitted Successfully!</h4>
              <p className="text-sm text-green-700">
                Thank you for your feedback. Our team will review and respond shortly.
              </p>
              <button
                onClick={() => setCommentSuccess(false)}
                className="mt-4 text-sm text-green-600 hover:text-green-800 underline"
              >
                Submit another comment
              </button>
            </div>
          ) : (
            <form onSubmit={handleCommentSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={commentForm.name}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your name"
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="comment-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={commentForm.email}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="comment-email-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Your Comment / Query *
                </label>
                <textarea
                  required
                  rows={4}
                  value={commentForm.comment}
                  onChange={(e) => setCommentForm(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Enter your questions, concerns, or feedback about this quotation..."
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  data-testid="comment-text-input"
                />
              </div>
              
              {commentError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {commentError}
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={commentSubmitting}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="comment-submit-btn"
                >
                  {commentSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Submit Comment
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* ===================== CLIENT: ACCEPT & CONFIRM SECTION ===================== */}
        {!isVendorView && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 mt-6 border-2 border-green-200" data-testid="accept-section">
            <h3 className="font-bold text-xl text-green-800 mb-4 flex items-center gap-2">
              <ShieldCheck size={24} className="text-green-600" />
              Accept & Confirm Quotation
            </h3>
            
            {acceptSuccess ? (
              <div className="bg-green-100 border border-green-300 rounded-lg p-6 text-center">
                <CheckCircle size={56} className="text-green-600 mx-auto mb-4" />
                <h4 className="font-bold text-xl text-green-800 mb-2">Quotation Accepted!</h4>
                <p className="text-green-700 mb-4">
                  Thank you, {acceptSuccess.acceptance?.accepted_by || acceptSuccess.accepted_by}. Your acceptance has been recorded.
                </p>
                
                {/* Show token invoice details - handle both new acceptance and already accepted cases */}
                {acceptSuccess.token_invoice ? (
                  <div className="bg-white rounded-lg p-4 mt-4 text-left space-y-2 border border-green-200">
                    <h5 className="font-semibold text-green-800 flex items-center gap-2">
                      <FileCheck size={18} /> Token Invoice Generated
                    </h5>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-stone-600">Invoice Number:</span>
                      <span className="font-medium">{acceptSuccess.token_invoice?.invoice_number}</span>
                      <span className="text-stone-600">Token Amount ({acceptSuccess.token_invoice?.percentage}%):</span>
                      <span className="font-bold text-green-700">
                        ₹{acceptSuccess.token_invoice?.amount?.toLocaleString('en-IN')}
                      </span>
                      <span className="text-stone-600">Due Date:</span>
                      <span className="font-medium">
                        {acceptSuccess.token_invoice?.due_date ? formatDate(new Date(acceptSuccess.token_invoice.due_date)) : 'N/A'}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500 mt-2 pt-2 border-t">
                      <Lock size={12} className="inline mr-1" />
                      Work will commence after token payment is received.
                    </p>
                  </div>
                ) : acceptSuccess.already_accepted ? (
                  <div className="bg-white rounded-lg p-4 mt-4 text-left border border-green-200">
                    <p className="text-sm text-stone-600">
                      <Lock size={14} className="inline mr-1" />
                      This quotation was already accepted on {acceptSuccess.accepted_at ? formatDate(new Date(acceptSuccess.accepted_at)) : 'a previous date'}.
                      Token invoice has already been generated. Please contact the company for payment details.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <form onSubmit={handleAcceptBOQ} className="space-y-4">
                <p className="text-green-700 text-sm mb-4">
                  By accepting this quotation, you confirm your agreement to proceed with the project. 
                  A token invoice will be generated upon acceptance.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">
                      Your Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={acceptForm.name}
                      onChange={(e) => setAcceptForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-2.5 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      data-testid="accept-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={acceptForm.email}
                      onChange={(e) => setAcceptForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="w-full px-4 py-2.5 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      data-testid="accept-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-green-800 mb-1">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    rows={2}
                    value={acceptForm.notes}
                    onChange={(e) => setAcceptForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special instructions or notes..."
                    className="w-full px-4 py-2.5 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none bg-white"
                    data-testid="accept-notes-input"
                  />
                </div>
                
                {acceptError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {acceptError}
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <strong>Note:</strong> Upon acceptance, a 5% token payment invoice will be generated. 
                  Work execution will begin after token payment is received.
                </div>
                
                <div className="flex justify-center pt-2">
                  <button
                    type="submit"
                    disabled={acceptSubmitting}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl text-lg"
                    data-testid="accept-submit-btn"
                  >
                    {acceptSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={20} />
                        Accept & Confirm
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        {/* ===================== VENDOR: ACCEPT SCOPE SECTION ===================== */}
        {isVendorView && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg p-6 mt-6 border-2 border-blue-200" data-testid="vendor-accept-section">
            <h3 className="font-bold text-xl text-blue-800 mb-4 flex items-center gap-2">
              <FileCheck size={24} className="text-blue-600" />
              Accept Scope of Work
            </h3>
            
            {vendorAcceptSuccess ? (
              <div className="bg-blue-100 border border-blue-300 rounded-lg p-6 text-center">
                <CheckCircle size={56} className="text-blue-600 mx-auto mb-4" />
                <h4 className="font-bold text-xl text-blue-800 mb-2">Scope Accepted!</h4>
                <p className="text-blue-700 mb-4">
                  Thank you, {vendorAcceptSuccess.acceptance?.vendor_name}. Your acceptance has been recorded.
                </p>
                <div className="bg-white rounded-lg p-4 mt-4 text-sm border border-blue-200">
                  <p className="flex items-center justify-center gap-2 text-blue-800">
                    <Lock size={16} /> Your rates are now locked for this project.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleVendorAcceptScope} className="space-y-4">
                <p className="text-blue-700 text-sm mb-4">
                  By accepting this scope, you confirm your commitment to deliver the work as specified. 
                  Your rates will be locked upon acceptance.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    Your Name / Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={vendorAcceptForm.name}
                    onChange={(e) => setVendorAcceptForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your name or company name"
                    className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    data-testid="vendor-accept-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    rows={2}
                    value={vendorAcceptForm.notes}
                    onChange={(e) => setVendorAcceptForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any notes or clarifications..."
                    className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white"
                    data-testid="vendor-accept-notes-input"
                  />
                </div>
                
                {acceptError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {acceptError}
                  </div>
                )}
                
                <div className="flex justify-center pt-2">
                  <button
                    type="submit"
                    disabled={vendorAcceptSubmitting}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl text-lg"
                    data-testid="vendor-accept-submit-btn"
                  >
                    {vendorAcceptSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FileCheck size={20} />
                        Accept Scope
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ===================== FOOTER ===================== */}
        <div className="text-center py-6 space-y-2">
          <p className="text-sm text-stone-500">
            This is a system-generated quotation from {company.name}
          </p>
          {company.address && (
            <p className="flex items-center justify-center gap-1 text-xs text-stone-400">
              <MapPin size={12} /> {company.address}
            </p>
          )}
          <p className="text-xs text-stone-400">
            Generated on {formatDate(new Date(), { longMonth: true })}
          </p>
        </div>

        {/* iter-386u-bt — Universal Consultancy Billing Legend
            iter-386u-bx — Public view is now strictly READ-ONLY. The current
            billable milestone is set by the admin from Edit BOQ; this section
            visually highlights the locked stage and its computed amount. */}
        {!isVendorView && (
          <ConsultancyBillingSchedule
            className="mt-8"
            schedule={data?.consultancy_billing_schedule}
            stage={data?.consultancy_stage}
            fee={data?.overhead_consultancy}
            basicTotal={data?.subtotal}
            siteBasic={data?.actual_subtotal}
            publicToken={token}
          />
        )}
      </div>
    </div>
  );
}
